// external
import React, { FC, useState } from 'react';
import {
  Typography,
  TextField,
  Box,
  CircularProgress,
  Input,
  InputAdornment,
  Select,
  MenuItem as MuiMenuItem,
  FormControl,
} from '@mui/material';

// internal
import useService from '../../../hooks/service.hook';
import EquipmentService from '../../../services/equipments.service';
import useSnackbarHelper from '../../../hooks/snackbar.hook';
import { palette } from '../../../theme';

interface EquipmentDetailsTabProps {
  equipmentId?: number;
  loading: boolean;
  currentEquipment: any;
  equipmentData: {
    name: string;
    brand: string;
    year: string;
    standardFieldValues: { [key: string]: string };
  };
  equipmentConfig: {
    selectedType: any | null;
    selectedSubtype: any | null;
    subtypes: any[];
    equipmentTypes: any[];
    standardFields: any[];
  };
  onEquipmentUpdate: (equipment: any) => void;
  onSetRefreshParent: (refresh: boolean) => void;
}

const EMPTY_PLACEHOLDER = '-- --';

function extractFieldEntry(raw: unknown): { value: string; unit: string } {
  if (raw != null && typeof raw === 'object' && 'value' in raw) {
    const obj = raw as { value?: unknown; measurement_unit?: string | null };
    return {
      value: obj.value != null ? String(obj.value) : '',
      unit: obj.measurement_unit ?? '',
    };
  }
  return { value: raw != null ? String(raw) : '', unit: '' };
}

const normalizeTypeName = (name: string | undefined): string =>
  String(name ?? '')
    .trim()
    .toLowerCase();

/** Keys treated as "flex/measurement" fields for XC ski grouping */
const FLEX_KEYS = new Set([
  'Camber Height',
  'MF 0.1',
  'MF 0.2',
  'MF 0.3',
  'Closed',
  'Closing Pressure',
  'Open',
  'Mid Ratio',
  'Kick Zone 0.2',
  'Kick Zone 0.4',
  'Kick Zone 0.6',
]);

/** Keys treated as "construction" fields for shoe grouping */
const SHOE_CONSTRUCTION_KEYS = new Set(['Foam Type', 'Stack Height', 'Drop', 'Lug Depth', 'Plated']);

/** Keys treated as "performance profile" fields for shoe grouping */
const SHOE_PERFORMANCE_KEYS = new Set(['Cushioning', 'Terrain Type', 'Type of Run', 'Foot Protection']);

interface FieldDef {
  key: string;
  label: string;
  editKey: string;
  value: string;
  displayValue: string;
  unit: string;
  options: string[];
  isSubtype?: boolean;
}

interface SectionDef {
  title: string;
  fields: FieldDef[];
}

const EquipmentDetailsTab: FC<EquipmentDetailsTabProps> = ({
  equipmentId,
  loading,
  currentEquipment,
  equipmentData,
  equipmentConfig,
  onEquipmentUpdate,
  onSetRefreshParent,
}) => {
  const equipmentService = useService(EquipmentService);
  const { showSnackbar } = useSnackbarHelper();

  const [editingKey, setEditingKey] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState<string>('');
  const [savingKey, setSavingKey] = useState<string | null>(null);

  const humanizeLabel = (key: string) => key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
  const toTrimmedString = (value: unknown) => (value == null ? '' : String(value)).trim();

  const getCurrentValueForKey = (key: string) => {
    if (key === 'name') return equipmentData.name ?? '';
    if (key === 'brand') return equipmentData.brand ?? '';
    if (key === 'year') return equipmentData.year ?? '';
    if (key === 'subtype')
      return equipmentConfig.selectedSubtype?.id != null ? String(equipmentConfig.selectedSubtype.id) : '';
    if (key.startsWith('standard:')) return equipmentData.standardFieldValues?.[key.slice('standard:'.length)] ?? '';
    return '';
  };

  const cancelInlineEdit = () => {
    setEditingKey(null);
    setDraftValue('');
  };

  const startInlineEdit = (key: string, value: unknown) => {
    if (savingKey) return;
    setEditingKey(key);
    setDraftValue(value == null ? '' : String(value));
  };

  const handleInlineKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      (e.target as HTMLInputElement).blur();
      return;
    }
    if (e.key === 'Escape') {
      e.preventDefault();
      cancelInlineEdit();
    }
  };

  const saveInlineEdit = (key: string, overrideValue?: string) => {
    if (!equipmentId) return cancelInlineEdit();
    const currentValue = getCurrentValueForKey(key);
    const nextValue = overrideValue !== undefined ? overrideValue : draftValue;
    if (toTrimmedString(nextValue) === toTrimmedString(currentValue)) return cancelInlineEdit();

    let payload: any = null;
    if (key === 'name') {
      const next = nextValue.trim();
      if (!next) return showSnackbar('Equipment name is required.', 'error');
      payload = { name: next };
    } else if (key === 'brand') {
      const next = nextValue.trim();
      if (!next) return showSnackbar('Brand is required.', 'error');
      payload = { brand: next };
    } else if (key === 'year') {
      const next = nextValue.trim();
      if (!/^\d{4}$/.test(next)) return showSnackbar('Year must be a 4-digit year (e.g., 2001).', 'error');
      payload = { year: parseInt(next, 10) };
    } else if (key === 'subtype') {
      payload = { equipment_subtype_id: nextValue ? parseInt(nextValue, 10) : null };
    } else if (key.startsWith('standard:')) {
      const fieldName = key.slice('standard:'.length);
      payload = { standard_field_values: { ...equipmentData.standardFieldValues, [fieldName]: nextValue.trim() } };
    }
    if (!payload) return cancelInlineEdit();

    setSavingKey(key);
    equipmentService
      .patchEquipmentV2(equipmentId, payload)
      .then((res: any) => {
        onSetRefreshParent(true);
        onEquipmentUpdate(res.data.data);
        showSnackbar('Saved', 'success');
      })
      .catch((err: any) => {
        showSnackbar(err?.response?.data?.message || 'Failed to save changes. Please try again later.', 'error');
      })
      .finally(() => {
        setSavingKey(null);
        cancelInlineEdit();
      });
  };

  const inlineInputSx = {
    '& .MuiInputBase-root': { fontSize: 13, color: palette.text.primary },
    '& .MuiInputBase-input': { textAlign: 'right' as const, padding: 0 },
    '& input::placeholder': { color: palette.text.disabled, opacity: 1 },
  };

  const renderInlineInput = (params: {
    fieldKey: string;
    ariaLabel: string;
    isSaving: boolean;
    inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
    unit?: string;
  }) => (
    <TextField
      variant="standard"
      value={draftValue}
      placeholder={EMPTY_PLACEHOLDER}
      onChange={(e) => setDraftValue(e.target.value)}
      onKeyDown={handleInlineKeyDown}
      autoFocus
      disabled={params.isSaving}
      onBlur={() => saveInlineEdit(params.fieldKey)}
      inputProps={{ 'aria-label': params.ariaLabel, ...params.inputProps }}
      InputProps={{
        disableUnderline: true,
        endAdornment: params.isSaving ? (
          <InputAdornment position="end">
            <CircularProgress size={12} />
          </InputAdornment>
        ) : params.unit ? (
          <InputAdornment position="end">
            <Typography sx={{ fontSize: 12, color: palette.text.secondary }}>{params.unit}</Typography>
          </InputAdornment>
        ) : undefined,
      }}
      sx={inlineInputSx}
    />
  );

  const renderInlineSelect = (params: {
    fieldKey: string;
    ariaLabel: string;
    isSaving: boolean;
    options: string[];
    value: string;
  }) => (
    <Box sx={{ position: 'relative' }}>
      <FormControl variant="standard" disabled={params.isSaving}>
        <Select
          value={params.value || ''}
          onChange={(e) => saveInlineEdit(params.fieldKey, e.target.value as string)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelInlineEdit();
            }
          }}
          autoFocus
          displayEmpty
          renderValue={(v) => v || ''}
          input={<Input disableUnderline />}
          inputProps={{ 'aria-label': params.ariaLabel }}
          sx={{ '& .MuiSelect-select': { textAlign: 'right', fontSize: 13, color: palette.text.primary, padding: 0 } }}
        >
          {params.options.map((opt: string) => (
            <MuiMenuItem key={opt} value={opt}>
              {opt}
            </MuiMenuItem>
          ))}
        </Select>
      </FormControl>
      {params.isSaving && (
        <InputAdornment
          position="end"
          sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        >
          <CircularProgress size={12} />
        </InputAdornment>
      )}
    </Box>
  );

  const renderInlineSubtypeSelect = (params: { fieldKey: string; ariaLabel: string; isSaving: boolean }) => (
    <Box sx={{ position: 'relative' }}>
      <FormControl variant="standard" disabled={params.isSaving}>
        <Select
          value={draftValue || ''}
          onChange={(e) => saveInlineEdit(params.fieldKey, String(e.target.value))}
          onKeyDown={(e) => {
            if (e.key === 'Escape') {
              e.preventDefault();
              cancelInlineEdit();
            }
          }}
          autoFocus
          displayEmpty
          renderValue={(value) => {
            if (!value) return EMPTY_PLACEHOLDER;
            const subtype = equipmentConfig.subtypes.find((s) => String(s.id) === String(value));
            return subtype?.name || '';
          }}
          input={<Input disableUnderline />}
          inputProps={{ 'aria-label': params.ariaLabel }}
          sx={{ '& .MuiSelect-select': { textAlign: 'right', fontSize: 13, color: palette.text.primary, padding: 0 } }}
        >
          <MuiMenuItem value="">
            <em>None</em>
          </MuiMenuItem>
          {equipmentConfig.subtypes.map((subtype) => (
            <MuiMenuItem key={subtype.id} value={String(subtype.id)}>
              {subtype.name}
            </MuiMenuItem>
          ))}
        </Select>
      </FormControl>
      {params.isSaving && (
        <InputAdornment
          position="end"
          sx={{ position: 'absolute', right: 0, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}
        >
          <CircularProgress size={12} />
        </InputAdornment>
      )}
    </Box>
  );

  /** Render a single label-value row */
  const renderRow = (field: FieldDef) => {
    const isEditing = editingKey === field.editKey;
    const isSaving = savingKey === field.editKey;
    const hasValue = field.value.trim().length > 0;

    return (
      <Box
        key={field.editKey}
        onClick={
          isEditing
            ? undefined
            : () =>
                startInlineEdit(
                  field.editKey,
                  field.isSubtype ? equipmentConfig.selectedSubtype?.id ?? '' : field.value
                )
        }
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 1.5,
          py: 0.75,
          cursor: equipmentId ? 'text' : 'default',
          borderBottom: `1px solid ${palette.grey[100]}`,
          '&:last-child': { borderBottom: 'none' },
          '&:hover': equipmentId ? { bgcolor: palette.grey[50] } : {},
          minHeight: 36,
          gap: 2,
        }}
      >
        <Typography sx={{ fontSize: 12.5, color: palette.text.secondary, flexShrink: 0 }}>{field.label}</Typography>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', minWidth: 0 }}>
          {isEditing ? (
            field.isSubtype ? (
              renderInlineSubtypeSelect({ fieldKey: field.editKey, ariaLabel: field.label, isSaving })
            ) : field.options.length > 0 ? (
              renderInlineSelect({
                fieldKey: field.editKey,
                ariaLabel: field.label,
                isSaving,
                options: field.options,
                value: field.value,
              })
            ) : (
              renderInlineInput({
                fieldKey: field.editKey,
                ariaLabel: field.label,
                isSaving,
                unit: field.unit || undefined,
              })
            )
          ) : (
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: hasValue ? 600 : 400,
                color: hasValue ? palette.text.primary : palette.text.disabled,
                textAlign: 'right',
              }}
            >
              {hasValue ? field.displayValue : EMPTY_PLACEHOLDER}
            </Typography>
          )}
        </Box>
      </Box>
    );
  };

  const renderSection = (title: string, fields: FieldDef[]) => {
    if (fields.length === 0) return null;
    return (
      <Box sx={{ mb: 2 }}>
        <Typography
          sx={{
            fontSize: 11,
            fontWeight: 700,
            color: palette.text.secondary,
            textTransform: 'uppercase',
            letterSpacing: 0.6,
            mb: 0.5,
            px: 0.5,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            border: `1px solid ${palette.divider}`,
            borderRadius: '10px',
            overflow: 'hidden',
            bgcolor: palette.background.paper,
          }}
        >
          {fields.map(renderRow)}
        </Box>
      </Box>
    );
  };

  /** Build FieldDef from a standard field config entry */
  const buildStandardFieldDef = (field: any): FieldDef => {
    const label = field.required ? `${humanizeLabel(field.name)} *` : humanizeLabel(field.name);
    const value = equipmentData.standardFieldValues?.[field.name] ?? '';
    const unit: string =
      field.measurement_unit ?? extractFieldEntry(currentEquipment?.standard_field_values?.[field.name]).unit;
    const options: string[] = field.type === 'dropdown' && field.options?.options ? field.options.options : [];
    const displayValue = value && unit ? `${value} ${unit}` : value;
    return {
      key: field.name,
      label,
      editKey: `standard:${field.name}`,
      value,
      displayValue,
      unit,
      options,
    };
  };

  /** Build FieldDef from an "extra" key not in standardFields config */
  const buildExtraFieldDef = (key: string): FieldDef => {
    const value = equipmentData.standardFieldValues?.[key] ?? '';
    const unit = extractFieldEntry(currentEquipment?.standard_field_values?.[key]).unit;
    const displayValue = value && unit ? `${value} ${unit}` : value;
    return {
      key,
      label: humanizeLabel(key),
      editKey: `standard:${key}`,
      value,
      displayValue,
      unit,
      options: [],
    };
  };

  /** Core fields always shown first */
  const coreFields: FieldDef[] = [
    {
      key: 'name',
      label: 'Equipment Name',
      editKey: 'name',
      value: equipmentData.name,
      displayValue: equipmentData.name,
      unit: '',
      options: [],
    },
    {
      key: 'brand',
      label: 'Manufacturer',
      editKey: 'brand',
      value: equipmentData.brand,
      displayValue: equipmentData.brand,
      unit: '',
      options: [],
    },
    {
      key: 'year',
      label: 'Year',
      editKey: 'year',
      value: equipmentData.year,
      displayValue: equipmentData.year,
      unit: '',
      options: [],
    },
  ];

  const subtypeField: FieldDef | null =
    equipmentConfig.subtypes.length > 0
      ? {
          key: 'subtype',
          label: 'Subtype',
          editKey: 'subtype',
          value: equipmentConfig.selectedSubtype?.name ?? '',
          displayValue: equipmentConfig.selectedSubtype?.name ?? '',
          unit: '',
          options: [],
          isSubtype: true,
        }
      : null;

  const typeName = equipmentConfig.selectedType?.name ?? currentEquipment?.equipment_type?.name ?? '';
  const isSkiType = normalizeTypeName(typeName) === 'xc skis';
  const isShoeType = normalizeTypeName(typeName) === 'shoes';

  const buildSections = (): SectionDef[] => {
    if (!equipmentId) return [];

    const allStandardFields = equipmentConfig.standardFields.map(buildStandardFieldDef);
    const extraKeys = Object.keys(equipmentData.standardFieldValues || {})
      .filter((k) => !equipmentConfig.standardFields.some((f: any) => f?.name === k))
      .sort((a, b) => a.localeCompare(b));
    const extraFields = extraKeys.map(buildExtraFieldDef);
    const allFields = [...allStandardFields, ...extraFields];

    if (isSkiType) {
      const infoFields = [
        ...coreFields,
        ...(subtypeField ? [subtypeField] : []),
        ...allFields.filter((f) => !FLEX_KEYS.has(humanizeLabel(f.key)) && !FLEX_KEYS.has(f.label.replace(' *', ''))),
      ];
      const flexFields = allFields.filter(
        (f) => FLEX_KEYS.has(humanizeLabel(f.key)) || FLEX_KEYS.has(f.label.replace(' *', ''))
      );
      return [
        { title: 'Equipment Info', fields: infoFields },
        { title: 'Flex & Measurements', fields: flexFields },
      ];
    }

    if (isShoeType) {
      const infoFields = [
        ...coreFields,
        ...(subtypeField ? [subtypeField] : []),
        ...allFields.filter(
          (f) =>
            !SHOE_CONSTRUCTION_KEYS.has(humanizeLabel(f.key)) &&
            !SHOE_CONSTRUCTION_KEYS.has(f.label.replace(' *', '')) &&
            !SHOE_PERFORMANCE_KEYS.has(humanizeLabel(f.key)) &&
            !SHOE_PERFORMANCE_KEYS.has(f.label.replace(' *', ''))
        ),
      ];
      const constructionFields = allFields.filter(
        (f) => SHOE_CONSTRUCTION_KEYS.has(humanizeLabel(f.key)) || SHOE_CONSTRUCTION_KEYS.has(f.label.replace(' *', ''))
      );
      const performanceFields = allFields.filter(
        (f) => SHOE_PERFORMANCE_KEYS.has(humanizeLabel(f.key)) || SHOE_PERFORMANCE_KEYS.has(f.label.replace(' *', ''))
      );
      return [
        { title: 'Shoe Info', fields: infoFields },
        { title: 'Construction', fields: constructionFields },
        { title: 'Performance Profile', fields: performanceFields },
      ];
    }

    // Generic: single "Equipment Info" section
    const infoFields = [...coreFields, ...(subtypeField ? [subtypeField] : []), ...allFields];
    return [{ title: 'Equipment Info', fields: infoFields }];
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!equipmentId) {
    const hasValues = equipmentData.standardFieldValues && Object.keys(equipmentData.standardFieldValues).length > 0;
    return (
      <Box sx={{ width: '100%' }} id="equipment-modal-description">
        {hasValues ? (
          <Box
            sx={{
              border: `1px solid ${palette.divider}`,
              borderRadius: '10px',
              overflow: 'hidden',
              bgcolor: palette.background.paper,
            }}
          >
            {Object.entries(equipmentData.standardFieldValues)
              .sort(([a], [b]) => a.localeCompare(b))
              .map(([key, value]) => {
                const hasVal = toTrimmedString(value).length > 0;
                return (
                  <Box
                    key={key}
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      px: 1.5,
                      py: 0.75,
                      borderBottom: `1px solid ${palette.grey[100]}`,
                      '&:last-child': { borderBottom: 'none' },
                    }}
                  >
                    <Typography sx={{ fontSize: 12.5, color: palette.text.secondary }}>{humanizeLabel(key)}</Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: hasVal ? 500 : 400,
                        color: hasVal ? palette.text.primary : palette.text.disabled,
                      }}
                    >
                      {hasVal ? toTrimmedString(value) : EMPTY_PLACEHOLDER}
                    </Typography>
                  </Box>
                );
              })}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ color: palette.text.secondary, fontSize: 13 }}>
            No details
          </Typography>
        )}
      </Box>
    );
  }

  const sections = buildSections();

  return (
    <Box sx={{ width: '100%' }} id="equipment-modal-description">
      {currentEquipment && currentEquipment.is_owner === false && currentEquipment.owner && (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ color: palette.text.secondary, fontSize: 12.5 }}>
            Owned by: {currentEquipment.owner.firstname} {currentEquipment.owner.lastname}
          </Typography>
        </Box>
      )}
      {sections.map((section) => renderSection(section.title, section.fields))}
    </Box>
  );
};

export default EquipmentDetailsTab;
